// Weiches Auf-/Zuklappen für Accordions (<details>) und ein-/ausblendbare
// Formularbereiche ([hidden], z.B. Buchungsformular). Animiert die Höhe per
// Web Animations API; bei prefers-reduced-motion wird ohne Animation umgeschaltet.
// Während des Zuklappens bleibt [open] bzw. die Sichtbarkeit bis zum Ende der
// Animation erhalten und das Element trägt data-collapse-closing (Chevron, _accordion.scss).
const DURATION = 300;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const CLOSING = 'data-collapse-closing';

const ACCORDION_SELECTOR = [
  'details.bfw-accordion__item',
  'details.itspoon-faq__item',
  '.faq-group-questions > details',
].join(', ');

const running = new WeakMap();

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function stop(element) {
  running.get(element)?.cancel();
  running.delete(element);
}

function animate(element, keyframes, onFinish) {
  stop(element);
  element.style.overflow = 'hidden';
  const animation = element.animate(keyframes, { duration: DURATION, easing: EASING });
  running.set(element, animation);
  animation.onfinish = () => {
    if (running.get(element) !== animation) return;
    running.delete(element);
    element.style.overflow = '';
    onFinish?.();
  };
}

// Höhe eines <details> nur mit <summary> (Padding/Rahmen des Elements eingerechnet)
function closedDetailsHeight(details) {
  const style = getComputedStyle(details);
  const summary = details.querySelector(':scope > summary');
  return ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
    .reduce((height, property) => height + parseFloat(style[property]), summary?.offsetHeight ?? 0);
}

export function isDetailsOpen(details) {
  return details.open && !details.hasAttribute(CLOSING);
}

export function setDetailsOpen(details, open) {
  if (open === isDetailsOpen(details)) return;

  if (prefersReducedMotion()) {
    stop(details);
    details.style.overflow = '';
    details.removeAttribute(CLOSING);
    details.open = open;
    return;
  }

  const from = details.offsetHeight;
  stop(details);

  if (open) {
    details.removeAttribute(CLOSING);
    details.open = true;
    animate(details, [{ height: `${from}px` }, { height: `${details.offsetHeight}px` }]);
  } else {
    details.setAttribute(CLOSING, '');
    animate(details, [{ height: `${from}px` }, { height: `${closedDetailsHeight(details)}px` }], () => {
      details.removeAttribute(CLOSING);
      details.open = false;
    });
  }
}

export function isShown(element) {
  return !element.hidden && !element.hasAttribute(CLOSING);
}

// Blendet ein Element über das hidden-Attribut weich ein/aus.
// animate: false schaltet sofort (z.B. Initialzustand), onDone läuft nach dem Ausblenden.
export function setHidden(element, hidden, { animate: animated = true, onDone } = {}) {
  if (hidden !== isShown(element)) {
    if (hidden) onDone?.();
    return;
  }

  if (!animated || prefersReducedMotion()) {
    stop(element);
    element.style.overflow = '';
    element.removeAttribute(CLOSING);
    element.hidden = hidden;
    if (hidden) onDone?.();
    return;
  }

  const collapsed = { height: '0px', marginTop: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };
  const from = element.hidden ? collapsed : { height: `${element.offsetHeight}px`, opacity: getComputedStyle(element).opacity };
  stop(element);

  if (hidden) {
    element.setAttribute(CLOSING, '');
    animate(element, [from, collapsed], () => {
      element.removeAttribute(CLOSING);
      element.hidden = true;
      onDone?.();
    });
  } else {
    element.removeAttribute(CLOSING);
    element.hidden = false;
    const style = getComputedStyle(element);
    const expanded = {
      height: `${element.offsetHeight}px`,
      marginTop: style.marginTop,
      marginBottom: style.marginBottom,
      paddingTop: style.paddingTop,
      paddingBottom: style.paddingBottom,
      opacity: 1,
    };
    animate(element, [{ ...expanded, ...from }, expanded]);
  }
}

export function initCollapse() {
  document.querySelectorAll(ACCORDION_SELECTOR).forEach((details) => {
    details.querySelector(':scope > summary')?.addEventListener('click', (event) => {
      event.preventDefault();
      setDetailsOpen(details, !isDetailsOpen(details));
    });
  });
}
